import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Edit, X, Loader } from 'lucide-react';
import {
  fetchFooterContact,
  updateFooterContact
} from '../../lib/sanityClient';
import SuccessToast from './SuccessToast';
import ErrorAlert from './ErrorAlert';

export default function AdminContact({ refreshTrigger = 0 }) {
  const [data, setData] = useState({
    email: '',
    phone: '',
    address: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editData, setEditData] = useState({ ...data });
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  const loadData = async () => {
    try {
      setLoading(true);
      const contactData = await fetchFooterContact();
      if (contactData) {
        setData(contactData);
        setEditData(contactData);
      }
      setError('');
    } catch (err) {
      setError('Kunne ikke laste kontaktinformasjon');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditData(data);
    setIsEditing(true);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      // Validate
      if (editData.email.trim() && !validateEmail(editData.email)) {
        setError('Ugyldig e-postadresse');
        setSubmitLoading(false);
        return;
      }

      await updateFooterContact(editData);
      setSuccessMessage('Kontaktinformasjon oppdatert!');
      await loadData();
      setIsEditing(false);
      setError('');
    } catch (err) {
      setError(err.message || 'En feil oppstod');
      console.error(err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-Pink-main"></div>
      </div>
    );
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={itemVariants}>
      <ErrorAlert message={error} onClose={() => setError('')} />

      <h2 className="text-2xl font-semibold text-Neautral-main font-opensans mb-6">
        Kontaktinformasjon
      </h2>

      {/* Edit Mode */}
      {isEditing ? (
        <motion.div
          className="bg-Pink-BG rounded-[15px] p-4 sm:p-6 md:p-8 border-2 border-Pink-main max-w-2xl"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-Neautral-main font-opensans">
              Rediger Kontaktinformasjon
            </h3>
            <motion.button
              onClick={() => setIsEditing(false)}
              className="text-Neautral-light hover:text-Neautral-main transition"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <X size={24} strokeWidth={2} />
            </motion.button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                E-postadresse
              </label>
              <input
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                placeholder="kontakt@studiosoraa.no"
                className="w-full px-4 py-2 rounded-[10px] border-2 border-Neautral-base bg-Neautral-white text-Neautral-main placeholder-Neautral-light focus:outline-none focus:border-Pink-main transition font-opensans"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                Telefon
              </label>
              <input
                type="tel"
                value={editData.phone}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                placeholder="+47 XXX XX XXX"
                className="w-full px-4 py-2 rounded-[10px] border-2 border-Neautral-base bg-Neautral-white text-Neautral-main placeholder-Neautral-light focus:outline-none focus:border-Pink-main transition font-opensans"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                Adresse
              </label>
              <input
                type="text"
                value={editData.address}
                onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                placeholder="Gateadresse, bysted"
                className="w-full px-4 py-2 rounded-[10px] border-2 border-Neautral-base bg-Neautral-white text-Neautral-main placeholder-Neautral-light focus:outline-none focus:border-Pink-main transition font-opensans"
              />
            </div>

            {/* Buttons */}
            <div className="flex flex-col-reverse sm:flex-row gap-4 justify-end pt-4">
              <motion.button
                type="button"
                onClick={() => setIsEditing(false)}
                disabled={submitLoading}
                className="px-6 py-2 rounded-[10px] bg-Neautral-base text-Neautral-main font-semibold font-opensans hover:bg-Neautral-light transition disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Avbryt
              </motion.button>
              <motion.button
                type="submit"
                disabled={submitLoading}
                className="px-6 py-2 rounded-[10px] bg-Pink-main text-white font-semibold font-opensans hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {submitLoading ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    <span className="hidden sm:inline">Lagrer...</span>
                    <span className="sm:hidden">Lagrer</span>
                  </>
                ) : (
                  'Lagre endringer'
                )}
              </motion.button>
            </div>
          </form>
        </motion.div>
      ) : (
        /* View Mode */
        <motion.div
          className="bg-Neautral-white rounded-[15px] p-4 sm:p-6 md:p-8 shadow-[0px_10px_25px_0px_rgba(126,126,126,0.15)] max-w-2xl"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
            {/* Email */}
            <div>
              <h3 className="text-xs sm:text-sm font-semibold text-Neautral-light font-opensans mb-2">
                E-postadresse
              </h3>
              <p className="text-base sm:text-lg text-Neautral-main font-opensans">
                <a href={`mailto:${data.email}`} className="text-Pink-main hover:underline break-all">
                  {data.email}
                </a>
              </p>
            </div>

            {/* Phone */}
            <div>
              <h3 className="text-xs sm:text-sm font-semibold text-Neautral-light font-opensans mb-2">
                Telefon
              </h3>
              <p className="text-base sm:text-lg text-Neautral-main font-opensans">
                <a href={`tel:${data.phone}`} className="text-Pink-main hover:underline">
                  {data.phone}
                </a>
              </p>
            </div>

            {/* Address */}
            <div>
              <h3 className="text-xs sm:text-sm font-semibold text-Neautral-light font-opensans mb-2">
                Adresse
              </h3>
              <p className="text-base sm:text-lg text-Neautral-main font-opensans">
                {data.address}
              </p>
            </div>
          </div>

          <motion.button
            onClick={handleEdit}
            className="flex items-center gap-2 px-6 py-3 rounded-[15px] bg-Pink-main text-white font-semibold font-opensans hover:opacity-90 transition"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Edit size={20} strokeWidth={2} />
            Rediger
          </motion.button>
        </motion.div>
      )}

      {/* Success Toast */}
      <SuccessToast
        message={successMessage}
        isVisible={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </motion.div>
  );
}
